import type { CSS } from '../../stitches.config';
import type { SpacingScale } from './sizes';
import { sizing } from '../../common/sizing';

export type GutterContext = 'application' | 'content' | 'section';
export type GutterPlacement = 'inside' | 'outside';
export type GutterSpacing = SpacingScale;
export type GutterEdge =
  | 'none'
  | 'all'
  | 'vertical'
  | 'horizontal'
  | 'top'
  | 'end'
  | 'bottom'
  | 'start';

/**
 * Subtract spacing tokens from gutter padding on the corresponding axis using `calc(token − $n)`.
 * Use when an inner child already has padding so outer gutter should align with its content.
 */
export type GutterAdjustment = {
  ends?: SpacingScale;
  sides?: SpacingScale;
};

export type GutterConfig = {
  context?: GutterContext;
  edge?: GutterEdge;
  placement?: GutterPlacement;
  adjustment?: GutterAdjustment;
};

function adjustGutterToken(token: string, subtract?: SpacingScale): string {
  if (subtract === undefined) return token;
  return `calc(${token} - $${subtract})`;
}

export type GutterProp = {
  gutter?: GutterConfig;
};

type GutterContextTokens = { ends: { inside: string; outside: string }; sides: string };

/** Single source of truth for ends (top/bottom) and sides (left/right) tokens per context. */
const GUTTER_CONTEXT_TOKENS: Record<GutterContext, GutterContextTokens> = {
  section: {
    ends: {
      inside: '$4',
      outside: '$4',
    },
    sides: sizing.sides,
  },
  content: {
    ends: {
      inside: '$8',
      outside: sizing.contentEnds,
    },
    sides: sizing.contentSides,
  },
  application: {
    ends: {
      inside: '$12',
      outside: sizing.ends,
    },
    sides: sizing.sides,
  },
};

const directChild = '& > *';

/**
 * Returns the token strings used for top/bottom (ends) and left/right (sides) padding for the
 * given context and gutter. Matches {@link gutterCSS} when passed the same `adjustment`.
 * Useful when another effect (e.g. fade mask) should align with the gutter padding.
 */
export function getGutterTokens(
  context: GutterContext = 'application',
  edge: GutterEdge = 'none',
  placement: GutterPlacement = 'outside',
  adjustment?: GutterAdjustment,
): { ends: string; sides: string } {
  if (edge === 'none') {
    return { ends: '$0', sides: '$0' };
  }
  const row = GUTTER_CONTEXT_TOKENS[context];
  const endsRaw = placement === 'inside' ? row.ends.inside : row.ends.outside;
  return {
    ends: adjustGutterToken(endsRaw, adjustment?.ends),
    sides: adjustGutterToken(row.sides, adjustment?.sides),
  };
}

function getGutterOutside(tokens: { ends: string; sides: string }, edge: GutterEdge): CSS {
  switch (edge) {
    case 'all':
      return { padding: `${tokens.ends} ${tokens.sides}` };
    case 'vertical':
      return { padding: `${tokens.ends} 0` };
    case 'horizontal':
      return { padding: `0 ${tokens.sides}` };
    case 'top':
      return { paddingTop: tokens.ends };
    case 'bottom':
      return { paddingBottom: tokens.ends };
    case 'start':
      return { paddingLeft: tokens.sides };
    case 'end':
      return { paddingRight: tokens.sides };
    case 'none':
    default:
      return { padding: 0 };
  }
}

function getGutterInside(endsInside: string, sides: string, edge: GutterEdge): CSS {
  switch (edge) {
    case 'all':
      return {
        [directChild]: { padding: `${endsInside} ${sides}` },
      };
    case 'vertical':
      return {
        [directChild]: { padding: `${endsInside} 0` },
      };
    case 'horizontal':
      return { [directChild]: { padding: `0 ${sides}` } };
    case 'top':
      return {
        [directChild]: { paddingTop: endsInside },
      };
    case 'bottom':
      return {
        [directChild]: { paddingBottom: endsInside },
      };
    case 'start':
      return { [directChild]: { paddingLeft: sides } };
    case 'end':
      return { [directChild]: { paddingRight: sides } };
    case 'none':
    default:
      return { [directChild]: { padding: 0 } };
  }
}

export const gutterCSS = ({
  context = 'application',
  edge = 'all',
  placement = 'outside',
  adjustment,
}: GutterConfig = {}): CSS => {
  if (edge === 'none') {
    return getGutterOutside({ ends: '$0', sides: '$0' }, edge);
  }
  const tokens = getGutterTokens(context, edge, placement, adjustment);
  if (placement === 'inside') {
    return getGutterInside(tokens.ends, tokens.sides, edge);
  }
  return getGutterOutside(tokens, edge);
};
